import { computed, inject, unref, type InjectionKey, type Ref } from 'vue';
import type { CustomFormsStorageContext } from '../components/custom_forms/types';

/**
 * Injection key for custom forms document upload context.
 * Apps that render document/multi-document custom form fields must provide
 * this so uploads can target the correct storage endpoint.
 * Can provide a plain object or a Ref for reactivity.
 *
 * @example
 * ```ts
 * // In app (e.g. Juno CheckoutDetailsPage)
 * import { CUSTOM_FORMS_STORAGE_CONTEXT_KEY } from '@satellite/composables/useCustomFormsStorageContext';
 *
 * const storageContext = computed(() => ({
 *   warehouseId: warehouse.value?.id ?? '',
 *   storageEndpoint: 'checkout/public/storage'
 * }));
 * provide(CUSTOM_FORMS_STORAGE_CONTEXT_KEY, storageContext);
 * ```
 */
export const CUSTOM_FORMS_STORAGE_CONTEXT_KEY: InjectionKey<
  CustomFormsStorageContext | Ref<CustomFormsStorageContext>
> = Symbol('customFormsStorageContext');

/**
 * Injects the custom forms storage context for document upload fields.
 * Throws if the app has not provided context or the context is invalid
 * (missing warehouseId or storageEndpoint). When provided value is a Ref,
 * returns a computed that stays reactive.
 */
export function useCustomFormsStorageContext(): Ref<CustomFormsStorageContext> {
  const injected = inject(CUSTOM_FORMS_STORAGE_CONTEXT_KEY);

  return computed(() => {
    if (injected == null) {
      throw new Error(
        'Custom forms storage context was not provided. Provide CUSTOM_FORMS_STORAGE_CONTEXT_KEY with warehouseId and storageEndpoint when using document upload fields.'
      );
    }

    const ctx = unref(injected);
    if (!ctx?.warehouseId || !ctx?.storageEndpoint) {
      throw new Error(
        'Custom forms storage context is invalid: warehouseId and storageEndpoint are required.'
      );
    }

    return ctx;
  });
}
